import React, {useEffect, useState} from "react";
import {Document, Image, Line, Page, pdf, Svg, Text, View} from "@react-pdf/renderer";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import {buttonStyle} from "../../../../../common/constants/buttonStyle";
import {useFormContext} from "react-hook-form";
import Html from 'react-pdf-html';
import {useQuery} from "@apollo/client";
import {
    GetDonationCertificateByIdDocument
} from "../../../../../../packages/graphql-layer/src/queries/__generated__/GetDonationCertificateById";
import {MEDIA_PATH} from "../../../../../common/constants/routes";
import {getCertificateBackgroundUri} from "../../../../../utility/cdnPictureUtil";
import {sosDonationCertificate} from "../../../../page_layouts/pageLayoutsUtils";
import {imageToBase64, svgToDataUri} from "../../../../../utility/imageUtil";
import {SosDonationCertificateType} from "../../../../types";
import {
    FIRSTNAME,
    GIFT_CERTIFICATE_PDF_DATA,
    GIFT_FIRSTNAME,
    GIFT_PERSONAL_GREETING,
    GIFT_SURNAME,
    PRODUCT_FIRSTNAME,
    PRODUCT_GREETING,
    PRODUCT_QUANTITY,
    PRODUCT_SURNAME,
    SURNAME,
    useTranslate
} from "../../utils";
import {QRCodeSVG} from "qrcode.react";
import {renderToString} from "react-dom/server";
import {initializeWithFallback} from "../../../../../utility/stringUtil";
import PdfWaterMark from "../../../../../utility/pdfWaterMark";
import {binderStyleSheet, giftCertificateStyle, productStyleSheet} from "./GiftCertificatePdfStyle";

interface Props {
    isValid?: boolean,
    pdfCertificate: string | undefined;
    qrCodeLink?: string;
    isProduct?: boolean;
    productBinderTemplate?: string;
}

export type GiftCertificatePdfData = {
    background: string;
    title: string;
    description: string;
    personalGreeting: string;
    productInfo: string;
    productIcon: string;
    binder: string;
    viewType: string;
};

const REQUIRED_VALUES = [FIRSTNAME, SURNAME, GIFT_FIRSTNAME, GIFT_SURNAME];

export function createProductPdf(pdfData: GiftCertificatePdfData, withWatermark: boolean,
                                 qrCodeData?: string) {
    const svgDataUri = initializeWithFallback(qrCodeData);
    return <Document>
        <Page size='A4'>
            <View style={giftCertificateStyle.backgroundContainer}>
                {/* eslint-disable-next-line jsx-a11y/alt-text */}
                <Image style={giftCertificateStyle.backgroundImage} src={pdfData.background}/>
            </View>
            {withWatermark && <PdfWaterMark text='MUSTER'/>}
            <View style={giftCertificateStyle.productBody}>
                <View style={giftCertificateStyle.productTitle}>
                    <Text>{pdfData.title}</Text>
                    <Svg height={40}>
                        <Line x1='0' y1='10' x2='140' y2='10' strokeWidth={5}
                              stroke="rgb(93, 161, 39)"/>
                    </Svg>
                </View>
                <View style={giftCertificateStyle.productContent}>
                    <Html stylesheet={productStyleSheet}>{pdfData.description}</Html>
                    <Text
                        style={giftCertificateStyle.personalGreeting}>{pdfData.personalGreeting}</Text>
                </View>
                <View style={giftCertificateStyle.product}>
                    { }
                    {svgDataUri !== "" ?
                        <Image style={giftCertificateStyle.productIcon} src={svgDataUri}/> :
                        <Image style={giftCertificateStyle.productIcon} src={pdfData.productIcon}/>}
                    <Html style={giftCertificateStyle.productInfo}>{pdfData.productInfo}</Html>
                </View>
                <Html style={giftCertificateStyle.binder}
                      stylesheet={binderStyleSheet}>{pdfData.binder}</Html>
            </View>
        </Page>
    </Document>;
}


const replacePlaceholdersInTemplate = (description: string,
                                       getValues: (field: string) => any): string => {
    const source = typeof description === 'string' ? description : '';
    return source
        .replace("${firstname}", getValues(FIRSTNAME) || "")
        .replace("${surname}", getValues(SURNAME) || "")
        .replace("${giftFirstname}", getValues(GIFT_FIRSTNAME) || "")
        .replace("${giftSurname}", getValues(GIFT_SURNAME) || "")
        .replace("${productFirstname}", getValues(PRODUCT_FIRSTNAME) || "")
        .replace("${productQuantity}", getValues(PRODUCT_QUANTITY) || "")
        .replace("${productSurname}", getValues(PRODUCT_SURNAME) || "")
        .replace("${productGreeting}", getValues(PRODUCT_GREETING) || "")
        .replace('${priority}', getValues('priority') || "")
        .replace("${date}", new Date().toLocaleDateString(
            "de", {year: "numeric", month: "2-digit", day: "2-digit"})
        );
};

export const createPdfData = (
    getValues: (field: string) => any,
    content: SosDonationCertificateType,
    background: string,
    icon: string,
    binderTemplate?: string
): GiftCertificatePdfData => {
    const description = replacePlaceholdersInTemplate(content?.detailText?.text || "", getValues);
    const personalGreeting = getValues(GIFT_PERSONAL_GREETING) || "";

    // Determine binder source: prefer explicit template, otherwise fallback to certificate's binder rich text
    const hasTemplate = !!binderTemplate && binderTemplate.trim() !== "";
    const binderSource = hasTemplate ? binderTemplate : (((content as any)?.binder?.text) || "");

    const binderReplaced = replacePlaceholdersInTemplate(binderSource, getValues);

    const formatBinderTemplateHtml = (s: string): string => {
        const trimmed = (s || "").trim();
        if (!trimmed) return "";
        return `<p>${trimmed}</p>`;
    };
    const binder = hasTemplate ? formatBinderTemplateHtml(binderReplaced) : binderReplaced;

    return {
        background,
        title: content?.title || "Certificate",
        description,
        personalGreeting,
        productInfo: content?.productInfo?.text || "",
        productIcon: icon,
        binder,
        viewType: content.viewtype!
    };
}

export function createPdf(pdfData: GiftCertificatePdfData, withWatermark: boolean,
                          qrCodeData?: string) {
    const svgDataUri = initializeWithFallback(qrCodeData);
    return <Document>
        <Page size='A4'>
            <View style={giftCertificateStyle.backgroundContainer}>
                {/* eslint-disable-next-line jsx-a11y/alt-text */}
                <Image style={giftCertificateStyle.backgroundImage} src={pdfData.background}/>
            </View>
            {withWatermark && <PdfWaterMark text='MUSTER'/>}
            <View style={giftCertificateStyle.body}>
                <View style={giftCertificateStyle.title}>
                    <Text>{pdfData.title}</Text>
                    <Svg height={40}>
                        <Line x1='0' y1='10' x2='140' y2='10' strokeWidth={6}
                              stroke="rgb(93, 161, 39)"/>
                    </Svg>
                </View>
                <View style={giftCertificateStyle.content}>
                    <Html style={giftCertificateStyle.description}>{pdfData.description}</Html>
                    <Text
                        style={giftCertificateStyle.personalGreeting}>{pdfData.personalGreeting}</Text>
                </View>
                <View style={giftCertificateStyle.product}>
                    { }
                    {svgDataUri !== "" ?
                        <Image style={giftCertificateStyle.productIcon} src={svgDataUri}/> :
                        <Image style={giftCertificateStyle.productIcon} src={pdfData.productIcon}/>}
                    <Html style={giftCertificateStyle.productInfo}>{pdfData.productInfo}</Html>
                </View>
                <Html style={giftCertificateStyle.binder}
                      stylesheet={binderStyleSheet}>{pdfData.binder}</Html>
            </View>
        </Page>
    </Document>;
}


export async function convertSvgToDataUri(qrString: string) {
    const svg = (
        <QRCodeSVG value={qrString}/>
    )
    return await svgToDataUri(renderToString(svg));
}

const GiftCertificatePreview: React.FC<Props> = ({
                                                     pdfCertificate,
                                                     isValid,
                                                     qrCodeLink,
                                                     isProduct,
                                                     productBinderTemplate
                                                 }) => {
    const translate = useTranslate("gift_data");
    const {getValues, setValue} = useFormContext();
    const [content, setContent] = React.useState<SosDonationCertificateType | null>(null);
    const [background, setBackground] = React.useState<string | null>(null);
    const [icon, setIcon] = React.useState<string | null>(null);
    const [svgDataUri, setSvgDataUri] = useState<string>("");
    const qrString = qrCodeLink || "";

    useEffect(() => {
        if (qrString !== "") {
            const dataUri = convertSvgToDataUri(qrString);
            dataUri.then((dataUri) => setSvgDataUri(dataUri || ""))
        }
    }, [qrString])

    const contentId = pdfCertificate?.split('/')?.pop();

    useQuery(GetDonationCertificateByIdDocument, {
        variables: { contentId },
        skip: !contentId,
        fetchPolicy: "network-only",
        onError: (error) => console.error("Error loading content: ", error),
        onCompleted: (data) => {
            const contentData = data?.content?.content;
            if (contentData?.type === sosDonationCertificate) {
                setContent(contentData);
                imageToBase64(getCertificateBackgroundUri(contentData.picture), setBackground);
                imageToBase64(MEDIA_PATH + contentData?.globalFactsSettings?.teaser_icon, setIcon);
            }
        }
    });

    useEffect(() => {
        if (!content) {
            const previewContent = getValues('previewContent');
            if (previewContent) {
                setContent(previewContent);
                imageToBase64(getCertificateBackgroundUri(previewContent.picture), setBackground);
                imageToBase64(MEDIA_PATH + previewContent?.globalFactsSettings?.teaser_icon, setIcon);
            }
        }
        if (content && background && icon) {
            setValue(GIFT_CERTIFICATE_PDF_DATA, {content, background, icon});
        }
    }, [content, background, icon, setValue, getValues]);


    const openPdfPreview = () => {
        if (content && background && icon) {
            // Open a blank window synchronously to avoid Safari popup blocker
            const newWindow = typeof window !== 'undefined' ? window.open('', '_blank') : null;
            const pdfData = createPdfData(getValues, content, background, icon,
                                          productBinderTemplate);
            pdf(createPdf(pdfData, true)).toBlob().then(blob => {
                const url = URL.createObjectURL(blob);
                if (newWindow) {
                    // Navigate the pre-opened window to the blob URL
                    newWindow.location.href = url;
                }
            });
        }
    };

    const openPdfPreviewForProduct = () => {
        if (content && background && icon) {
            // Open a blank window synchronously to avoid Safari popup blocker
            const newWindow = typeof window !== 'undefined' ? window.open('', '_blank') : null;
            const pdfData = createPdfData(getValues, content, background, icon,
                                          productBinderTemplate);
            pdf(createProductPdf(pdfData, true, svgDataUri)).toBlob().then(blob => {
                const url = URL.createObjectURL(blob);
                if (newWindow) {
                    // Navigate the pre-opened window to the blob URL
                    newWindow.location.href = url;
                }
            });
        }
    };

    const invalidRequiredValues = () => REQUIRED_VALUES.some(field => !getValues(field));
    const isButtonDisabled = !content || !background || !icon || invalidRequiredValues();

    return (
        <>
            {isProduct ?
                <Button type="button"
                        data-cypress="giftCertificatePreview"
                        size="large"
                        variant="contained"
                        onClick={openPdfPreviewForProduct}
                        disabled={!isValid}
                        data-testid="giftCertificateProductPreview"
                        sx={{...buttonStyle.green}}
                        fullWidth>{translate('p020_product_label_Submit')!}</Button> :
                <Grid size={{xs: 12, md: 12}}>
                    <Button
                        data-cypress="giftCertificatePreview"
                        variant="contained"
                        onClick={openPdfPreview}
                        disabled={isButtonDisabled}
                        sx={{...buttonStyle.green}}
                    >{translate("gift_certificate_preview")}</Button>
                </Grid>
            }
        </>
    );
};

export default GiftCertificatePreview;
